import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from '../common';

function UploadModal({ isOpen, onClose, onUploadSuccess }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  const [validationOnly, setValidationOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // 파일 업로드
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('파일을 선택해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('validation_only', validationOnly);
      formData.append('create_missing', true);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/bulk-upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
          },
          body: formData
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '파일 업로드에 실패했습니다.');
      }

      const result = await response.json();
      setUploadResults(result.data);
      
      if (!validationOnly) {
        alert('파일 업로드가 완료되었습니다.');
        setSelectedFiles([]);
        // 업로드 성공 시 부모 컴포넌트에 알림
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        alert('파일 검증이 완료되었습니다.');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 파일 선택 핸들러
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 20) {
      alert('최대 20개 파일까지 선택 가능합니다.');
      return;
    }
    
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > 200 * 1024 * 1024) { // 200MB
      alert('전체 파일 크기는 200MB를 초과할 수 없습니다.');
      return;
    }

    setSelectedFiles(files);
    setUploadResults(null);
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setUploadResults(null);
    setValidationOnly(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="시험 파일 업로드"
      size="xl"
      preventScroll={true}
      zIndex={9999930}
      isMobileView={window.innerWidth < 768}
    >
      <div className="mb-4 rounded border border-blue-200 bg-blue-50 p-4">
        <h3 className="mb-2 font-semibold text-blue-800">파일명 규칙</h3>
        <p className="mb-2 text-sm text-blue-700">
          형식: "학교명,학교타입,시험연도,학년,학기,시험타입,[과목].pdf"
        </p>
        <p className="mb-2 text-sm text-blue-700">
          예시: "혜성여고,3,2025,1,1,1,수학.pdf" 또는 "혜성여고,3,2025,1,1,1.pdf"
        </p>
        <div className="text-sm text-blue-700">
          <p>• 학교타입: 1(초등), 2(중학), 3(고등)</p>
          <p>• 학년: 학교타입별 상대학년 (초등 1-6, 중학 1-3, 고등 1-3)</p>
          <p>• 학기: 1(1학기), 2(2학기)</p>
          <p>• 시험타입: 1(중간), 2(기말), 3(기타)</p>
          <p>• 과목: 선택사항</p>
          <p className="mt-2 text-xs text-blue-600">
            ※ 학년은 각 학교급별 상대학년으로 입력 (예: 고1→1, 중2→2)
          </p>
        </div>
      </div>
      
      <div className="rounded-3xl border-2 border-dashed border-gray-300 p-6 text-center">
        <input
          type="file"
          multiple
          accept=".pdf"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload-modal"
        />
        <label
          htmlFor="file-upload-modal"
          className="flex cursor-pointer flex-col items-center"
        >
          <Upload className="mb-4 size-12 text-gray-400" />
          <p className="mb-2 text-lg font-medium text-gray-600">
            파일을 선택하거나 드래그하여 업로드
          </p>
          <p className="text-sm text-gray-500">
            PDF 파일만 지원, 최대 20개 파일, 총 200MB 이하
          </p>
        </label>
      </div>

      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 font-semibold">선택된 파일 ({selectedFiles.length}개)</h3>
          <div className="max-h-40 overflow-y-auto rounded border p-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="py-1 text-sm">
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={validationOnly}
            onChange={(e) => setValidationOnly(e.target.checked)}
            className="mr-2"
          />
          검증만 수행
        </label>
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleFileUpload}
            disabled={selectedFiles.length === 0 || isLoading}
            loading={isLoading}
            className="bg-primary hover:bg-[#3da575]"
          >
            {validationOnly ? '검증하기' : '업로드하기'}
          </Button>
        </div>
      </div>

      {/* 업로드 결과 */}
      {uploadResults && (
        <div className="mt-6 border-t pt-6">
          <h3 className="mb-4 font-semibold">처리 결과</h3>
          
          {validationOnly ? (
            <div className="space-y-4">
              <div className="rounded border border-green-200 bg-green-50 p-4">
                <h4 className="font-semibold text-green-800">유효한 파일 ({uploadResults.valid_files?.length || 0}개)</h4>
                {uploadResults.valid_files?.map((file, index) => (
                  <div key={index} className="mt-1 text-sm text-green-700">
                    {file.filename}
                  </div>
                ))}
              </div>
              
              {uploadResults.validation_failed?.length > 0 && (
                <div className="rounded border border-red-200 bg-red-50 p-4">
                  <h4 className="font-semibold text-red-800">검증 실패 ({uploadResults.validation_failed.length}개)</h4>
                  {uploadResults.validation_failed.map((file, index) => (
                    <div key={index} className="mt-1 text-sm text-red-700">
                      {file.filename}: {file.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded bg-gray-100 p-4">
                <h4 className="font-semibold">요약</h4>
                <div className="mt-2 text-sm">
                  <p>전체 처리: {uploadResults.summary?.total_processed || 0}개</p>
                  <p>성공: {uploadResults.summary?.successful || 0}개</p>
                  <p>건너뜀: {uploadResults.summary?.skipped || 0}개</p>
                  <p>검증 실패: {uploadResults.summary?.validation_failed || 0}개</p>
                  <p>업로드 실패: {uploadResults.summary?.upload_failed || 0}개</p>
                </div>
              </div>

              {uploadResults.results?.successful?.length > 0 && (
                <div className="rounded border border-green-200 bg-green-50 p-4">
                  <h4 className="font-semibold text-green-800">성공한 파일</h4>
                  {uploadResults.results.successful.map((file, index) => (
                    <div key={index} className="mt-1 text-sm text-green-700">
                      {file.filename} (ID: {file.sxm_id})
                    </div>
                  ))}
                </div>
              )}

              {uploadResults.results?.validation_failed?.length > 0 && (
                <div className="rounded border border-red-200 bg-red-50 p-4">
                  <h4 className="font-semibold text-red-800">검증 실패한 파일</h4>
                  {uploadResults.results.validation_failed.map((file, index) => (
                    <div key={index} className="mt-1 text-sm text-red-700">
                      {file.filename}: {file.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default UploadModal; 